import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// True if a JWT's exp claim is in the past (or the token can't be read)
export const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return !payload.exp || payload.exp * 1000 <= Date.now();
  } catch {
    return true;
  }
};

// A request sent with a token that the server rejects means the session has
// expired: clear it and send the user back to log in, instead of leaving
// every page showing an error.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const sentToken = error.config?.headers?.Authorization;
    if (error.response?.status === 401 && sentToken && localStorage.getItem('user')) {
      localStorage.removeItem('user');
      if (window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    }
    return Promise.reject(error);
  }
);

// Get token from localStorage
const getAuthHeader = () => {
  const user = localStorage.getItem('user');
  if (user) {
    const userData = JSON.parse(user);
    if (userData.token) {
      return { Authorization: `Bearer ${userData.token}` };
    }
  }
  return {};
};

// Auth API
export const authAPI = {
  register: async (email, password, name, role) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      name,
      role
    });
    return response.data;
  },

  login: async (email, password, role) => {
    const response = await axios.post(`${API}/auth/login`, {
      email,
      password,
      role
    });
    return response.data;
  },

  googleLogin: async (googleToken, role) => {
    const response = await axios.post(`${API}/auth/google`, {
      google_token: googleToken,
      role
    });
    return response.data;
  },

  getMe: async () => {
    const response = await axios.get(`${API}/auth/me`, {
      headers: getAuthHeader()
    });
    return response.data;
  }
};

// Services API
export const servicesAPI = {
  getServices: async () => {
    const response = await axios.get(`${API}/services`);
    return response.data;
  },

  createServiceRequest: async (requestData) => {
    const response = await axios.post(`${API}/service-requests`, requestData, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getCustomerServiceRequests: async () => {
    const response = await axios.get(`${API}/service-requests/customer`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getServiceTracking: async (requestId) => {
    const response = await axios.get(`${API}/service-requests/${requestId}/tracking`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  cancelServiceRequest: async (requestId, reason) => {
    const response = await axios.post(`${API}/service-requests/${requestId}/cancel`, { reason }, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getServiceQuotes: async (requestId) => {
    const response = await axios.get(`${API}/service-requests/${requestId}/quotes`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  acceptQuote: async (requestId, quoteId) => {
    const response = await axios.post(`${API}/service-requests/${requestId}/accept-quote/${quoteId}`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  }
};

// Driver API
export const driverAPI = {
  getAvailableRequests: async () => {
    const response = await axios.get(`${API}/service-requests/available`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  acceptRequest: async (requestId) => {
    const response = await axios.put(`${API}/service-requests/${requestId}/accept`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  completeRequest: async (requestId) => {
    const response = await axios.put(`${API}/service-requests/${requestId}/complete`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  toggleOnline: async (isOnline) => {
    const response = await axios.put(`${API}/drivers/toggle-online`, {
      is_online: isOnline
    }, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  updateLocation: async (latitude, longitude) => {
    const response = await axios.put(`${API}/drivers/location`, {
      latitude,
      longitude
    }, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getVerificationStatus: async () => {
    const response = await axios.get(`${API}/drivers/verification`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  submitVerification: async (licenseNumber, licenseFrontFile, licenseBackFile, insuranceFile) => {
    const formData = new FormData();
    formData.append('license_number', licenseNumber);
    formData.append('license_document_front', licenseFrontFile);
    formData.append('license_document_back', licenseBackFile);
    formData.append('insurance_document', insuranceFile);
    const response = await axios.post(`${API}/drivers/verification`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  getEarnings: async () => {
    const response = await axios.get(`${API}/drivers/earnings`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getActiveJobs: async () => {
    const response = await axios.get(`${API}/drivers/active-jobs`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  submitQuote: async (serviceRequestId, quoteData) => {
    const response = await axios.post(`${API}/service-requests/${serviceRequestId}/submit-quote`, quoteData, {
      headers: getAuthHeader()
    });
    return response.data;
  }
};

// Payment API
export const paymentAPI = {
  createPaymentIntent: async (serviceRequestId, paymentMethod, immediateStartConsent) => {
    const response = await axios.post(`${API}/payments/create-intent`, {
      service_request_id: serviceRequestId,
      payment_method: paymentMethod,
      immediate_start_consent: immediateStartConsent
    }, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  confirmPayment: async (paymentIntentId, serviceRequestId) => {
    const response = await axios.post(`${API}/payments/confirm`, {
      payment_intent_id: paymentIntentId,
      service_request_id: serviceRequestId
    }, {
      headers: getAuthHeader()
    });
    return response.data;
  }
};

// Vehicle API
export const vehicleAPI = {
  isLookupAvailable: async () => {
    const response = await axios.get(`${API}/vehicles/lookup-available`);
    return response.data.available;
  },

  lookupVehicle: async (registration) => {
    const response = await axios.post(`${API}/vehicles/lookup`, {
      registration
    }, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  addVehicle: async (vehicle) => {
    const response = await axios.post(`${API}/vehicles`, vehicle, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getVehicles: async () => {
    const response = await axios.get(`${API}/vehicles`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  setPrimaryVehicle: async (vehicleId) => {
    const response = await axios.put(`${API}/vehicles/${vehicleId}/primary`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  deleteVehicle: async (vehicleId) => {
    const response = await axios.delete(`${API}/vehicles/${vehicleId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  }
};

// Membership API
export const membershipAPI = {
  getMembershipStatus: async () => {
    const response = await axios.get(`${API}/membership/status`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  upgradeMembership: async (tier) => {
    const response = await axios.post(`${API}/membership/upgrade`, {
      tier
    }, {
      headers: getAuthHeader()
    });
    return response.data;
  }
};

// Payout API
export const payoutAPI = {
  getBalance: async () => {
    const response = await axios.get(`${API}/payouts/balance`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getPreferences: async () => {
    const response = await axios.get(`${API}/payouts/preferences`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  updatePreferences: async (preferences) => {
    const response = await axios.put(`${API}/payouts/preferences`, preferences, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  requestWithdrawal: async (amount) => {
    const response = await axios.post(`${API}/payouts/withdraw`, {
      amount
    }, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getHistory: async () => {
    const response = await axios.get(`${API}/payouts/history`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  startConnectOnboarding: async () => {
    const response = await axios.post(`${API}/payouts/connect/onboard`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getConnectStatus: async () => {
    const response = await axios.get(`${API}/payouts/connect/status`, {
      headers: getAuthHeader()
    });
    return response.data;
  }
};

// Admin API
export const adminAPI = {
  getOverview: async () => {
    const response = await axios.get(`${API}/admin/overview`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getAllDrivers: async () => {
    const response = await axios.get(`${API}/admin/drivers`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getAllCustomers: async () => {
    const response = await axios.get(`${API}/admin/customers`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getAllServiceRequests: async (statusFilter = null) => {
    const url = statusFilter ? `${API}/admin/service-requests?status_filter=${statusFilter}` : `${API}/admin/service-requests`;
    const response = await axios.get(url, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getAllUsers: async (roleFilter = null, fromDate = null, toDate = null) => {
    let url = `${API}/admin/users`;
    const params = new URLSearchParams();
    if (roleFilter) params.append('role_filter', roleFilter);
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
    if (params.toString()) url += `?${params.toString()}`;
    
    const response = await axios.get(url, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getActiveJobs: async () => {
    const response = await axios.get(`${API}/admin/active-jobs`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  refundServiceRequest: async (requestId, amount, note) => {
    const response = await axios.post(`${API}/admin/service-requests/${requestId}/refund`, { amount, note }, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  resetPassword: async (userId, newPassword) => {
    const response = await axios.post(`${API}/admin/reset-password`, {
      user_id: userId,
      new_password: newPassword
    }, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  updateUser: async (userId, updateData) => {
    const response = await axios.put(`${API}/admin/user/${userId}`, updateData, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getCompletedJobs: async (fromDate = null, toDate = null) => {
    let url = `${API}/admin/completed-jobs`;
    const params = new URLSearchParams();
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
    if (params.toString()) url += `?${params.toString()}`;
    
    const response = await axios.get(url, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getVerifications: async (statusFilter = 'pending') => {
    const response = await axios.get(`${API}/admin/verifications?status_filter=${statusFilter}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  viewVerificationDocument: async (driverId, docType) => {
    // Open the tab synchronously while the click still counts as a user gesture -
    // opening it after the download finished got silently popup-blocked for
    // large files (e.g. multi-MB insurance PDFs).
    const win = window.open('', '_blank');
    if (win) win.document.title = 'Loading document…';
    try {
      const response = await axios.get(`${API}/admin/verifications/${driverId}/document/${docType}`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(response.data);
      if (win) {
        win.location.href = url;
      } else {
        // Popups fully blocked - fall back to downloading the file.
        const a = document.createElement('a');
        a.href = url;
        a.download = `${docType}`;
        a.click();
      }
    } catch (error) {
      if (win) win.close();
      throw error;
    }
  },

  approveDriver: async (driverId) => {
    const response = await axios.put(`${API}/admin/verifications/${driverId}/approve`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  rejectDriver: async (driverId, reason) => {
    const response = await axios.put(`${API}/admin/verifications/${driverId}/reject`, { reason }, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  setDriverMembership: async (driverId, tier) => {
    const response = await axios.put(`${API}/admin/drivers/${driverId}/membership`, { tier }, {
      headers: getAuthHeader()
    });
    return response.data;
  }
};

// Content API
export const contentAPI = {
  getTerms: async () => {
    const response = await axios.get(`${API}/content/terms`);
    return response.data;
  },

  updateTerms: async (content, notifyUsers = true) => {
    const response = await axios.put(`${API}/content/terms`, { content, notify_users: notifyUsers }, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getPrivacy: async () => {
    const response = await axios.get(`${API}/content/privacy`);
    return response.data;
  },

  updatePrivacy: async (content, notifyUsers = true) => {
    const response = await axios.put(`${API}/content/privacy`, { content, notify_users: notifyUsers }, {
      headers: getAuthHeader()
    });
    return response.data;
  }
};

// Rating API
export const ratingAPI = {
  submitRating: async (serviceRequestId, rating, comment = null) => {
    const response = await axios.post(`${API}/ratings/submit`, {
      service_request_id: serviceRequestId,
      rating,
      comment
    }, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  submitTip: async (serviceRequestId, amount) => {
    const response = await axios.post(`${API}/ratings/tip`, {
      service_request_id: serviceRequestId,
      amount
    }, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getServiceRatings: async (serviceRequestId) => {
    const response = await axios.get(`${API}/ratings/service/${serviceRequestId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getUserRatings: async (userId) => {
    const response = await axios.get(`${API}/ratings/user/${userId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  }
};

// Referral API
export const referralAPI = {
  getMyCode: async () => {
    const response = await axios.get(`${API}/referrals/my-code`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  applyCode: async (referralCode) => {
    const response = await axios.post(`${API}/referrals/apply`, {
      referral_code: referralCode
    }, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getBalance: async () => {
    const response = await axios.get(`${API}/referrals/balance`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getMyReferrals: async () => {
    const response = await axios.get(`${API}/referrals/my-referrals`, {
      headers: getAuthHeader()
    });
    return response.data;
  }
};
